import { css, useTheme, type Theme } from '@emotion/react';
import { transparentize } from 'polished';
import { Item as RadioGroupItem } from '@radix-ui/react-radio-group';
import { Warning } from '@phosphor-icons/react';
import { priceListPerHour } from 'common/accounting/priceList';
import { CoreInstanceType, CoreInstanceTypeSettings, CoreInstanceTypeTexts } from 'common/core/CoreInstanceConstants';
import { circleCheckIconURI } from 'client/components/RadioGroup/RadioGroupItem';
import { Tag } from 'client/components/Tag';
import { Typography } from 'client/components/Typography';
import { applyEffect } from 'client/Theme';
import { formatPrice } from '../deploymentUtils';

const checkCornerIconURI = (theme: Theme) =>
  `url("data:image/svg+xml,%3Csvg width='192' height='192' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cg ` +
  `clip-path='url(%23a)'%3E%3Cpath d='M0 0h192v192H0z'/%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' ` +
  `d='M192 192 0 0h192v192ZM162.68 47.06a6 6 0 0 0-8.49-8.49L126.06 66.7l-11.94-11.94a6 6 0 0 0-8.49 8.48l16.19 16.19a6 ` +
  `6 0 0 0 8.49 0l32.37-32.37Z' fill='${theme.color.primary[100].replace('#', '%23')}'/%3E%3C/g%3E%3Cdefs%3E%3CclipPath ` +
  `id='a'%3E%3Cpath d='M0 0h192v192H0z'/%3E%3C/clipPath%3E%3C/defs%3E%3C/svg%3E");`;

export interface VMSelectItemProps {
  item: CoreInstanceTypeSettings;
  isUnsupported?: boolean;
  showDevWarning?: boolean;
}

export const VMSelectItem = ({ item, isUnsupported, showDevWarning = false }: VMSelectItemProps) => {
  const { coreInstanceTypeID, name, memoryGiB, vCpu } = item;

  const pricePerHour = priceListPerHour.coreInstancePrice[coreInstanceTypeID];

  const theme = useTheme();
  return (
    <RadioGroupItem
      disabled={isUnsupported}
      value={`${coreInstanceTypeID}`}
      aria-label={`size ${name}, priced at ${pricePerHour} an hour`}
      css={itemStyle}
    >
      <div />
      <section>
        <section>
          {/* <Icon weight="duotone" size={36} color={isItemEnabled ? theme.color.primary[100] : theme.color.grey[100]} /> */}
          <section aria-hidden>
            <Typography variant="h5">{CoreInstanceTypeTexts[coreInstanceTypeID]}</Typography>
            {/* <Typography variant="body2">/ hour</Typography> */}
          </section>
          <ul>
            <Typography variant="body2" component="li">
              <b>{formatPrice(pricePerHour)}</b>
              <span>/ hour</span>
            </Typography>
            <Typography variant="body2" component="li">
              <b>{memoryGiB} GB</b>
              <span>Memory</span>
            </Typography>
            <Typography variant="body2" component="li">
              <b>{vCpu}</b>
              <span>vCPU</span>
            </Typography>
            {isUnsupported && (
              <li css={recommendationStyle}>
                <Tag>
                  <Warning weight="fill" color={theme.color.yellow[100]} />
                  <span style={{ color: theme.color.yellow[100] }}>N/A with this template</span>
                </Tag>
              </li>
            )}
            {showDevWarning && item.coreInstanceTypeID === CoreInstanceType.SMALL && (
              <li css={recommendationStyle}>
                <Tag>
                  <Warning weight="fill" /> Not recommended for dev
                </Tag>
              </li>
            )}
          </ul>
        </section>
      </section>
    </RadioGroupItem>
  );
};

const itemStyle = (theme: Theme) => css`
  position: relative;
  padding: 0;
  background-color: transparent;
  border: none;
  padding-left: 32px;
  outline: none;

  &:first-of-type {
    > div {
      top: 50%;
      height: calc(50% + 3px);
    }

    > section,
    > div {
      border-radius: ${theme.borderRadius}px ${theme.borderRadius}px 0 0;
    }
  }

  &:last-of-type {
    > div {
      height: calc(50%);
    }

    > section,
    > div {
      border-radius: 0 0 ${theme.borderRadius}px ${theme.borderRadius}px;
    }
  }

  &:disabled > section {
    padding: 13px;
    background-color: transparent;
    border: 3px solid ${theme.color.grey[60]};
    cursor: not-allowed;

    > p > .tag {
      margin-right: 6px;
      background-color: ${theme.color.grey[80]};
      color: ${theme.color.white} !important;
    }
  }

  &:focus-visible > section {
    outline: solid 4px ${theme.color.primary[100]};
    outline-offset: 4px;
    z-index: 2;
  }

  &:hover:not(:disabled):not([data-state='checked']) {
    &::after {
      border-right-color: ${applyEffect(theme.color.grey[60], 'hover')};
    }

    > section {
      background-color: ${applyEffect(theme.color.grey[60], 'hover')};
    }
  }

  &[data-state='checked'] {
    > div {
      background-image: linear-gradient(to top, ${theme.color.grey[60]} 50%, ${theme.color.primary[100]} 50%);
    }

    &:first-of-type > div {
      background-image: none;
      background-color: ${theme.color.grey[60]};
    }

    &:last-of-type > div {
      background-image: none;
      background-color: ${theme.color.primary[100]};
    }

    & ~ * > div {
      background-color: ${theme.color.grey[60]};
    }

    &::before {
      content: '';
      display: block;
      position: absolute;
      top: 50%;
      left: -11px;
      width: 24px;
      height: 24px;
      background-color: ${theme.color.background};
      background-image: ${circleCheckIconURI(theme)};
      background-size: 100%;
      outline: 2px solid ${theme.color.background};
      transform: translate(0.5px, -50%);
      z-index: 1;
    }

    &::after {
      border-right-color: ${theme.color.primary[60]};
    }

    > section {
      background-color: ${theme.color.primary[60]};

      > section > section {
        background-color: ${theme.color.primary[100]};
        border-color: transparent;
      }

      &::after {
        content: '';
        display: block;
        position: absolute;
        top: 0;
        right: 0;
        width: 32px;
        height: 32px;
        background-image: ${checkCornerIconURI(theme)};
        background-size: 100%;
      }
    }
  }

  &::after {
    content: '';
    position: absolute;
    display: block;
    top: 50%;
    left: calc(32px - 10px);
    width: 0;
    height: 0;
    border-top: 10px solid transparent;
    border-bottom: 10px solid transparent;
    border-right: 10px solid transparent;
    transform: translateY(-50%);
  }

  > div {
    position: absolute;
    left: 0;
    width: 3px;
    height: calc(100% + 3px);
    background-color: ${theme.color.primary[100]};
  }

  > section {
    display: flex;
    position: relative;
    flex-direction: column;
    align-items: flex-start;
    padding: 16px;
    background-color: ${theme.color.grey[60]};
    border: none;
    overflow: hidden;
    cursor: pointer;
    z-index: 1;

    > section {
      display: flex;
      align-items: center;

      > svg {
        margin: 0 4px;
      }

      > section {
        margin-left: 8px;
        width: 56px;
        text-align: center;
        border: 2px solid ${transparentize(0.95, theme.color.foreground)};
        border-radius: ${theme.borderRadius}px;

        > h5 {
          transform: translateY(2px);
        }
      }

      > ul {
        display: grid;
        grid-template-columns: repeat(3, 80px) 1fr;
        grid-template-rows: repeat(1, 1fr);
        grid-gap: 4px;
        justify-items: left;
        list-style: none;
        margin: 2px 0;

        > li {
          text-align: left;

          > b {
            color: ${theme.color.foreground};
          }

          > span {
            display: block;
          }
        }
      }
    }
  }
`;

const recommendationStyle = css`
  position: absolute;
  right: 16px;
  align-self: center;

  > .tag {
    display: flex;
    align-items: center;

    > svg {
      margin-left: -1px;
      margin-right: 3px;
    }
  }
`;
